import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useBoats, useCreateBoat } from '@/features/inventory/hooks'
import { useAdoptableInspections } from '@/features/inspection/hooks'
import type { Inspection } from '@/types/inspection'
import { ClipboardCheck } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
} from '@tanstack/react-table'
import { useState, useEffect } from 'react'
import { Boat, ListingState } from '@/types/inventory'
import { useDebounce } from '@/components/hooks/use-debounce'
import { downscaleImage } from '@/lib/image'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Ship, Plus, Handshake, BadgeDollarSign } from 'lucide-react'
import { BoatForm, BoatFormValues } from '@/features/inventory/boat-form'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import {
  BoatPhotoPicker,
  type PendingBoatPhoto,
} from '@/features/inventory/components/boat-photo-picker'
import { boatColumns } from '@/features/inventory/components/boat-columns'
import { uploadBoatImage, UPLOAD_CONCURRENCY } from '@/features/inventory/api'
import { mapWithConcurrency } from '@/lib/concurrency'
import { AnimatedTabs, type AnimatedTab } from '@/components/customers/animated-tabs'
import { ConsignmentsTab } from '@/features/consignment/components/consignments-tab'
import { SoldTab } from '@/features/inventory/components/sold-tab'
import { requireFlow } from '@/features/auth/guards'

// The Boats tab shows unsold boats only — sold ones move to the Sold tab.
const LISTING_FILTERS = ['all', 'for_sale', 'off_market'] as const
type ListingFilter = (typeof LISTING_FILTERS)[number]

function isListingFilter(value: unknown): value is ListingFilter {
  return typeof value === 'string' && (LISTING_FILTERS as readonly string[]).includes(value)
}

const INVENTORY_TABS = ['boats', 'sold', 'consignments'] as const
type InventoryTab = (typeof INVENTORY_TABS)[number]

function isInventoryTab(value: unknown): value is InventoryTab {
  return typeof value === 'string' && (INVENTORY_TABS as readonly string[]).includes(value)
}

export const Route = createFileRoute('/_authenticated/inventory/')({
  beforeLoad: requireFlow('inventory'),
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: InventoryTab; listing?: ListingFilter; q?: string } => {
    const base: { tab?: InventoryTab; listing?: ListingFilter; q?: string } = {
      tab: isInventoryTab(search.tab) ? search.tab : undefined,
      q: typeof search.q === 'string' ? search.q : undefined,
    }
    if (isListingFilter(search.listing)) base.listing = search.listing
    return base
  },
  component: InventoryPage,
})

// Defined at module scope so the table doesn't recreate columns per render.
const columns = boatColumns()

// Stable identity for the loading state: TanStack Table reruns its row models
// (and resets pagination state) whenever `data` changes identity, so a fresh
// `[]` on every render would loop forever while the query is in flight.
const EMPTY_BOATS: Boat[] = []

function InventoryPage() {
  const { tab } = Route.useSearch()
  const navigate = useNavigate({ from: '/inventory/' })
  const activeTab: InventoryTab = tab ?? 'boats'

  const setActiveTab = (next: string) => {
    if (!isInventoryTab(next)) return
    // Keep the boats tab's search/listing params so a tab round-trip restores
    // the previous view; the sold/consignments tabs keep their filters in memory.
    navigate({
      search: (prev) => ({ ...prev, tab: next === 'boats' ? undefined : next }),
    })
  }

  const tabs: AnimatedTab[] = [
    { value: 'boats', label: 'Boats', icon: Ship, content: <BoatsTab /> },
    {
      value: 'sold',
      label: 'Sold',
      icon: BadgeDollarSign,
      content: <SoldTab />,
    },
    {
      value: 'consignments',
      label: 'Consignments',
      icon: Handshake,
      content: <ConsignmentsTab />,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Ship className="h-8 w-8" /> Inventory
        </h1>
        <p className="text-muted-foreground">
          Track which boats are available for sale, review sold boats, and manage consignment
          agreements.
        </p>
      </div>

      <AnimatedTabs tabs={tabs} value={activeTab} onValueChange={setActiveTab} />
    </div>
  )
}

function BoatsTab() {
  const { listing, q: urlQ } = Route.useSearch()
  const navigate = useNavigate({ from: '/inventory/' })
  const activeFilter: ListingFilter = listing ?? 'all'

  const [searchInput, setSearchInput] = useState(urlQ ?? '')
  const debouncedQ = useDebounce(searchInput, 300)

  useEffect(() => {
    navigate({
      search: (prev) => {
        const next: Record<string, string | undefined> = { ...prev }
        if (debouncedQ) {
          next.q = debouncedQ
        } else {
          delete next.q
        }
        return next
      },
      replace: true,
    })
  }, [debouncedQ, navigate])

  // Only pass the filter to the API when it isn't "all" — saves the backend a scan.
  const apiListingState: ListingState | undefined =
    activeFilter === 'all' ? undefined : (activeFilter as ListingState)

  // Sold boats never appear in this tab — they live in the Sold tab — and the
  // backend filters them server-side so client-side search stays consistent.
  const { data, isLoading, isError } = useBoats(apiListingState, debouncedQ || undefined, 'sold')
  const [createOpen, setCreateOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const createMutation = useCreateBoat()
  const qc = useQueryClient()
  const [pendingPhotos, setPendingPhotos] = useState<PendingBoatPhoto[]>([])
  const [photoProgress, setPhotoProgress] = useState<{ done: number; total: number } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  // Intake inspection adopted onto the created boat (its record and photos
  // move over; the sparse intake boat is cleaned up server-side).
  const [adoptedInspection, setAdoptedInspection] = useState<Inspection | null>(null)
  const { data: adoptableInspections } = useAdoptableInspections()
  // Same pool as the Inspection page: every inspection not yet attached to a
  // work order.
  const adoptable = adoptableInspections ?? []

  // Fallback client-side filter for when q is empty (keeps existing UX snappy)
  const isClientFilter = !debouncedQ
  const [sorting, setSorting] = useState<SortingState>([])
  const table = useReactTable({
    data: data ?? EMPTY_BOATS,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isClientFilter ? getFilteredRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      ...(isClientFilter ? { globalFilter: searchInput } : {}),
    },
    onGlobalFilterChange: isClientFilter ? setSearchInput : undefined,
  })

  const setListingFilter = (next: string) => {
    if (!isListingFilter(next)) return
    const search: Record<string, string | undefined> = { tab: undefined }
    if (next !== 'all') search.listing = next as ListingFilter
    if (debouncedQ) search.q = debouncedQ
    navigate({ search: () => search })
  }

  const addPendingPhotos = (files: File[]) => {
    if (!files.length) return
    setPendingPhotos((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        // Kick the downscale off now so it overlaps form-fill; the submit
        // handler awaits the already-running promise.
        processed: downscaleImage(file),
      })),
    ])
  }

  const removePendingPhoto = (id: string) => {
    setPendingPhotos((prev) => {
      const photo = prev.find((p) => p.id === id)
      if (photo) URL.revokeObjectURL(photo.previewUrl)
      return prev.filter((p) => p.id !== id)
    })
  }

  const clearPendingPhotos = () => {
    setPendingPhotos((prev) => {
      for (const p of prev) URL.revokeObjectURL(p.previewUrl)
      return []
    })
  }

  const handleCreateOpenChange = (open: boolean) => {
    setCreateOpen(open)
    if (!open) {
      clearPendingPhotos()
      setAdoptedInspection(null)
    }
  }

  // Warn before discarding a half-filled boat form (and its picked photos).
  const { guardOpenChange: guardCreateOpen, discardGuard: createDirtyGuard } = useDirtyClose(
    createOpen,
    handleCreateOpenChange,
    formDirty,
  )

  const handleCreate = async (values: BoatFormValues) => {
    const body = {
      make: values.make,
      model: values.model,
      year: values.year,
      price: values.price,
      cost: values.cost,
      length_ft: values.length_ft,
      status: values.status,
      listing_state: values.listing_state,
      condition: values.condition,
      type: values.type,
      color: values.color || '',
      description: values.description || '',
      engine_manufacturer:
        values.engine_manufacturer === 'none' || !values.engine_manufacturer
          ? ''
          : values.engine_manufacturer,
      engine_model: values.engine_model || '',
      engine_count: values.engine_count ?? 1,
      engine_serial: values.engine_serial || '',
      engine_hours: values.engine_hours === '' ? null : (values.engine_hours ?? null),
      hin: values.hin || '',
      customer_id: values.customer_id || undefined,
      inspection_id: adoptedInspection?.id,
    }
    setUploadError(null)
    try {
      const boat = await createMutation.mutateAsync(body)
      // Photos upload only after the boat exists; a failed upload must not
      // lose the created boat, so failures are counted and surfaced after.
      if (pendingPhotos.length > 0) {
        setPhotoProgress({ done: 0, total: pendingPhotos.length })
        let failed = 0
        const upload = async (photo: PendingBoatPhoto, sortOrder: number) => {
          try {
            await uploadBoatImage(boat.id, await photo.processed, sortOrder)
          } catch (err) {
            failed += 1
            console.error(`Failed to upload photo ${photo.file.name}:`, err)
          } finally {
            setPhotoProgress((p) => (p ? { ...p, done: p.done + 1 } : p))
          }
        }
        // The first photo uploads alone so it deterministically wins the
        // cover (the server crowns whichever image INSERTs first); the rest
        // go in parallel with explicit sort orders.
        const [first, ...rest] = pendingPhotos
        await upload(first, 1)
        await mapWithConcurrency(UPLOAD_CONCURRENCY, rest, (photo, i) => upload(photo, i + 2))
        setPhotoProgress(null)
        if (failed > 0) {
          setUploadError(
            `${failed} of ${pendingPhotos.length} photos failed to upload — the boat was saved; add the missing photos from its detail page.`,
          )
        }
      }
      qc.invalidateQueries({ queryKey: ['boats'] })
      setCreateOpen(false)
      clearPendingPhotos()
      setAdoptedInspection(null)
    } catch {
      // Create failed; createMutation.isError surfaces in the dialog and the
      // picked photos are kept for a retry.
      setPhotoProgress(null)
    }
  }

  if (isLoading) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (isError) {
    return <div className="text-destructive">Failed to load inventory.</div>
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            placeholder="Search boats…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full sm:max-w-sm"
          />
          <Select value={activeFilter} onValueChange={setListingFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filter by listing" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All listings</SelectItem>
              <SelectItem value="for_sale">For Sale</SelectItem>
              <SelectItem value="off_market">Off Market</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={createOpen} onOpenChange={guardCreateOpen}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Boat
          </Button>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Boat</DialogTitle>
              <DialogDescription>New boats default to For Sale and Available.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <ClipboardCheck className="h-4 w-4" /> Intake inspection
              </p>
              <Select
                value={adoptedInspection?.id ?? 'none'}
                onValueChange={(v) =>
                  setAdoptedInspection(adoptable.find((i) => i.id === v) ?? null)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None — start blank</SelectItem>
                  {adoptable.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.year} {i.make} {i.model} · {new Date(i.performed_at).toLocaleDateString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {adoptedInspection && (
                <p className="text-xs text-muted-foreground">
                  The form below prefilled from the intake
                  {adoptedInspection.hin ? ` (HIN ${adoptedInspection.hin})` : ''}; its photos move
                  onto this boat and the previous intake record is removed unless something else
                  still references it.
                </p>
              )}
            </div>
            <BoatForm
              key={adoptedInspection?.id ?? 'blank'}
              defaultValues={
                adoptedInspection
                  ? {
                      hin: adoptedInspection.hin || '',
                      year: adoptedInspection.year,
                      make: adoptedInspection.make,
                      model: adoptedInspection.model,
                      engine_manufacturer: adoptedInspection.engine_brand || '',
                      engine_model: adoptedInspection.engine_model || '',
                      engine_count: adoptedInspection.engine_count,
                      engine_serial: adoptedInspection.engine_serial || '',
                    }
                  : undefined
              }
              onDirtyChange={setFormDirty}
              onSubmit={handleCreate}
              isPending={createMutation.isPending || photoProgress !== null}
              submitLabel={
                photoProgress
                  ? `Uploading photos ${photoProgress.done}/${photoProgress.total}…`
                  : 'Create Boat'
              }
            >
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Photos</p>
                <BoatPhotoPicker
                  photos={pendingPhotos}
                  onAdd={addPendingPhotos}
                  onRemove={removePendingPhoto}
                />
                <p className="text-xs text-muted-foreground">
                  The first photo becomes the cover. Titles and categories can be set after
                  creation.
                </p>
              </div>
            </BoatForm>
            {createMutation.isError && (
              <p className="text-sm text-destructive">
                Failed to create boat: {createMutation.error.message}
              </p>
            )}
          </DialogContent>
        </Dialog>
        {createDirtyGuard}
      </div>

      {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No boats found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
